// Wave 2 — Material Price Intelligence list surface.
// Provider-neutral pricing framework. Every row is a REAL recorded
// observation with directive §7 provenance stamped. No fake prices.
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "../../components/AppShell";
import { api, formatApiError } from "../../lib/api";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import StatusChip from "../../components/StatusChip";
import { toast } from "sonner";
import { Plus, GitCompareArrows, ShieldCheck, FileSearch } from "lucide-react";

const SOURCE_LABELS = {
  vendor_portal: "Vendor Portal",
  manual_upload: "Manual Upload",
  marketplace_research: "Marketplace Research",
  home_depot_pro: "Home Depot Pro (Scaffold)",
  lowes_pro: "Lowe's Pro (Scaffold)",
  licensed_provider: "Licensed Provider (Scaffold)",
  direct_supplier: "Direct Supplier (Scaffold)",
};
const VERIFICATION_TONE = {
  verified: "connected",
  unverified: "phase_n",
  expired: "error",
  conflict: "error",
};

export default function MaterialPrices() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");

  useEffect(() => {
    api.get("/material-prices").then((r) => setRows(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, []);

  const filtered = useMemo(() => {
    let out = rows;
    if (sourceFilter) out = out.filter((r) => r.source_type === sourceFilter);
    if (q.trim()) {
      const s = q.toLowerCase();
      out = out.filter((r) =>
        (r.product_description || "").toLowerCase().includes(s) ||
        (r.store_or_supplier || "").toLowerCase().includes(s) ||
        (r.manufacturer || "").toLowerCase().includes(s)
      );
    }
    return out;
  }, [rows, q, sourceFilter]);

  const money = (n, cur) => `${cur || "USD"} ${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const dateShort = (iso) => (iso || "").slice(0, 10);

  return (
    <AppShell
      title="Material Price Intelligence"
      subtitle="Provider-neutral price capture. Every row is a real observation with recorded source, timestamp, and audit trail."
    >
      <div className="flex flex-wrap items-center gap-3 mb-4" data-testid="material-prices-toolbar">
        <Link to="/procurement/prices/new" data-testid="material-prices-new">
          <Button className="bg-erf-gold text-erf-dark hover:bg-erf-goldHover">
            <Plus className="w-4 h-4 mr-1.5" /> Record price
          </Button>
        </Link>
        <Link to="/procurement/prices/compare" data-testid="material-prices-compare">
          <Button variant="outline" className="border-erf-borderCream">
            <GitCompareArrows className="w-4 h-4 mr-1.5" /> Compare across sources
          </Button>
        </Link>
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search product, manufacturer, or supplier…"
          className="max-w-sm"
          data-testid="material-prices-search"
        />
        <div className="flex flex-wrap gap-1.5" data-testid="material-prices-source-filters">
          <button
            className={`px-2.5 py-1 text-xs font-mono rounded-md border ${sourceFilter === "" ? "bg-erf-dark text-erf-cream border-erf-dark" : "bg-white border-erf-borderCream text-erf-dark/70 hover:bg-erf-creamSecondary"}`}
            onClick={() => setSourceFilter("")}
            data-testid="filter-source-all"
          >
            All
          </button>
          {Object.entries(SOURCE_LABELS).map(([k, label]) => (
            <button
              key={k}
              className={`px-2.5 py-1 text-xs font-mono rounded-md border ${sourceFilter === k ? "bg-erf-dark text-erf-cream border-erf-dark" : "bg-white border-erf-borderCream text-erf-dark/70 hover:bg-erf-creamSecondary"}`}
              onClick={() => setSourceFilter(k)}
              data-testid={`filter-source-${k}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <Card className="border-erf-borderCream" data-testid="material-prices-empty">
          <CardContent className="p-8 text-center">
            <FileSearch className="w-8 h-8 text-erf-dark/40 mx-auto mb-2" />
            <div className="font-display font-semibold text-erf-dark mb-1">No prices recorded yet</div>
            <div className="text-sm font-nav text-erf-dark/60 max-w-md mx-auto">
              Add your first price via Vendor Portal, upload a quote, or record a research finding.
              Every observation is stamped with source, timestamp, and audit trail.
            </div>
            <div className="mt-4 flex justify-center gap-2">
              <Link to="/procurement/prices/new">
                <Button size="sm" className="bg-erf-gold text-erf-dark hover:bg-erf-goldHover">Record first price</Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3" data-testid="material-prices-list">
          {filtered.map((r) => (
            <Card key={r.id} className="border-erf-borderCream" data-testid={`price-row-${r.id}`}>
              <CardContent className="p-4 flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="text-[10px] font-mono uppercase tracking-widest text-erf-goldHover">
                      {SOURCE_LABELS[r.source_type] || r.source_type}
                    </span>
                    <StatusChip status={VERIFICATION_TONE[r.verification_status] || "phase_n"} label={r.verification_status} />
                    {r.audit_entry_id != null && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-mono text-erf-dark/60">
                        <ShieldCheck className="w-3 h-3" /> audit #{r.audit_entry_id}
                      </span>
                    )}
                  </div>
                  <div className="font-display font-semibold text-erf-dark truncate">{r.product_description}</div>
                  <div className="text-xs font-nav text-erf-dark/70 mt-1 truncate">
                    {r.manufacturer ? `${r.manufacturer} · ` : ""}
                    {r.model_sku ? `SKU ${r.model_sku} · ` : ""}
                    {r.store_or_supplier}
                    {r.supplier_location ? ` · ${r.supplier_location}` : ""}
                  </div>
                  <div className="text-[11px] font-mono text-erf-dark/50 mt-1">
                    observed {dateShort(r.observed_at)} · {r.source_provider_key}
                    {r.source_url ? " · has URL" : ""}
                    {r.source_document_id ? " · has receipt" : ""}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-mono text-xl text-erf-dark">{money(r.price_per_unit, r.currency)}</div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-erf-dark/60">per {r.unit_of_measure}</div>
                  <Link to={`/procurement/prices/${r.id}`} className="mt-2 inline-block">
                    <Button size="sm" variant="outline" className="border-erf-borderCream" data-testid={`price-open-${r.id}`}>
                      Open
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </AppShell>
  );
}
